const MYOB_OAUTH_STATE_STORAGE_PREFIX = 'di_myob_oauth_state_v1:'
export const MYOB_OAUTH_STATE_MAX_AGE_MS = 10 * 60 * 1000

type MyobOAuthStateRecord = {
  platformId: string
  createdAt: number
}

const STATE_PATTERN = /^[a-f0-9]{64}$/

function storageKey(state: string) {
  return `${MYOB_OAUTH_STATE_STORAGE_PREFIX}${state}`
}

export function createMyobOAuthState(platformId: string): string {
  if (!platformId.trim()) throw new Error('Unable to start MYOB authentication without a platform.')
  if (!globalThis.crypto?.getRandomValues) {
    throw new Error('Secure browser storage is required to connect MYOB. Please try another browser.')
  }

  const bytes = new Uint8Array(32)
  globalThis.crypto.getRandomValues(bytes)
  const state = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  const record: MyobOAuthStateRecord = { platformId, createdAt: Date.now() }

  try {
    globalThis.localStorage.setItem(storageKey(state), JSON.stringify(record))
  } catch {
    throw new Error('Secure browser storage is required to connect MYOB. Please enable storage and try again.')
  }

  return state
}

export function discardMyobOAuthState(state: string | null | undefined) {
  if (!state || !STATE_PATTERN.test(state)) return
  try {
    globalThis.localStorage.removeItem(storageKey(state))
  } catch {
    // Cleanup is best-effort when browser storage becomes unavailable.
  }
}

export function consumeMyobOAuthState(
  state: string | null | undefined,
  now = Date.now(),
): MyobOAuthStateRecord | null {
  if (!state || !STATE_PATTERN.test(state)) return null

  let raw: string | null
  try {
    raw = globalThis.localStorage.getItem(storageKey(state))
    if (raw !== null) globalThis.localStorage.removeItem(storageKey(state))
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<MyobOAuthStateRecord>
    if (typeof parsed.platformId !== 'string' || !parsed.platformId.trim()) return null
    if (typeof parsed.createdAt !== 'number' || !Number.isFinite(parsed.createdAt)) return null
    const age = now - parsed.createdAt
    if (age < 0 || age > MYOB_OAUTH_STATE_MAX_AGE_MS) return null
    return { platformId: parsed.platformId, createdAt: parsed.createdAt }
  } catch {
    return null
  }
}

export function myobOAuthStateStorageKey(state: string) {
  return storageKey(state)
}
